import React from 'react';
import FormCard from '../components/common/FormCard';

const UpgradeToPro = () => {
  // Free plan features
  const freeFeatures = [
    { text: '5 legal page templates', included: true },
    { text: 'Unlimited legal pages', included: true },
    { text: 'Dynamic shortcodes', included: true },
    { text: 'Rich text editor', included: true },
    { text: 'Popup system', included: false },
    { text: 'Cookie consent bar', included: false }
  ];

  // Pro plan features
  const proFeatures = [
    { text: '29 legal page templates', included: true },
    { text: 'Everything in Free', included: true },
    { text: 'Popup agreement system', included: true },
    { text: 'EU Cookie consent bar', included: true },
    { text: 'Service blocking', included: true },
    { text: 'Priority support', included: true }
  ];

  // Pro included features
  const proIncludedFeatures = [
    { text: '📄 All 29 legal page templates including GDPR, Medical, Affiliate' },
    { text: '💬 Create unlimited popup notices with full customization' },
    { text: '🍪 EU Cookie compliance bar with 6 position options' },
    { text: '🛡️ Block third-party cookies and tracking services' },
    { text: '✉️ Priority email support' },
    { text: '🔄 Regular template updates' },
    { text: '⚙️ Popup settings (size, behavior, auto-hide)' },
    { text: '🎨 Color customization for all elements' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="!max-w-[890px] mx-auto">

        {/* Header Section */}
        <header className="text-center mb-12">
          {/* Upgrade Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full mb-6">
            <span className="text-xl">👑</span>
            <span className="text-purple-600 text-[16px] font-semibold">Upgrade to Pro</span>
          </div>

          <h1 className="!text-[26px] sm:!text-[32px] md:!text-5xl font-semibold !font-bold !text-[#17171C] mb-4">
            Get the Full Legal Pages Experience
          </h1>

          <p className="w-full sm:w-3/4 md:w-1/2 text-center !mx-auto !my-[30px] !text-[14px] sm:!text-[16px] font-normal !text-[#6F6F7B]">
            Unlock 24 additional templates, popup system, EU cookie bar, and more powerful features for complete legal compliance.
          </p>
        </header>

        {/* Pricing Cards */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-16 max-w-5xl mx-auto">

          {/* Free Plan */}
          <FormCard>
            <div className="h-full flex flex-col p-4 sm:p-6">
              <h2 className="font-inter !font-semibold !text-[18px] !tracking-[0%] !text-[#17171C] align-middle">Free</h2>

              <p className="font-inter !font-normal !text-[14px] !leading-[20px] !tracking-[0%] text-gray-600 mb-6 align-middle">
                Essential features to get started
              </p>

              <div className="mb-6">
                <span className="text-4xl sm:text-5xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600 ml-2 text-sm sm:text-base">/forever</span>
              </div>

              <ul className="space-y-3 mb-8 flex-grow">
                {freeFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    {feature.included ? (
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className={feature.included ? 'text-gray-900' : 'text-gray-400'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#"
                className="w-full py-3 px-6 border-2 !border-[#E4E4E780] font-inter !text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors inline-block text-center"
              >
                Current Plan
              </a>
            </div>
          </FormCard>

          {/* Pro Plan */}
          <FormCard className="relative border-2 border-purple-200 !bg-gradient-to-t !from-[#7C3BED0D] !to-[#7C3BED0D]">
            {/* Recommended Badge */}
            <div className="absolute top-4 sm:top-5 right-4 sm:right-6">
              <span
                className="inline-flex items-center gap-1 px-3 sm:px-4 py-1 text-white text-xs sm:text-sm font-semibold rounded-full"
                style={{ background: 'linear-gradient(100.13deg, #7C3BED 0%, #9D26D9 100%)' }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                RECOMMENDED
              </span>
            </div>

            <div className="h-full flex flex-col p-4 sm:p-6">
              <h2 className="font-inter !font-semibold !text-[18px] !tracking-[0%] !text-[#17171C] align-middle">Pro</h2>

              <p className="text-gray-600 mb-6">Complete legal compliance solution</p>

              <div className="mb-6">
                <span className="text-4xl sm:text-5xl font-bold text-gray-900">$49</span>
                <span className="text-gray-600 ml-2 text-sm sm:text-base">/year</span>
              </div>

              <ul className="space-y-3 mb-8 flex-grow">
                {proFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-purple-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-900">{feature.text}</span>
                  </li>
                ))}
              </ul>

              <a
                href="https://wpwax.com/product/legal-pages-pro/#single-plugin-pricing-plan"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-purple-600 hover:bg-purple-700 !text-white text-base sm:text-lg font-semibold rounded-xl transition-colors shadow-lg shadow-purple-600/30 items-center justify-center"
              >
                <span className="text-lg sm:text-xl">👑</span>
                Upgrade to Pro
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
            </div>
          </FormCard>
        </section>

        {/* Everything Included in Pro */}
        <FormCard className="mb-12 p-4 sm:p-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">👑 Everything Included in Pro</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {proIncludedFeatures.map((feature, index) => (
              <div key={index} className="flex items-start gap-3 pl-3 bg-[#F4F4F5] text-opacity-50 !text-[#F4F4F580] rounded-xl transition-shadow p-2 sm:p-3">
                <p className="text-gray-700 text-[14px] sm:text-[15px]">{feature.text}</p>
              </div>
            ))}
          </div>
        </FormCard>

        {/* Footer CTA */}
        <footer className="text-center">
          <p className="text-gray-600 mb-6 text-sm sm:text-base">
            30-day money-back guarantee. No questions asked.
          </p>

          <a
            href="https://wpwax.com/product/legal-pages-pro/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-purple-600 hover:bg-purple-700 text-white text-base sm:text-lg font-semibold rounded-xl transition-colors shadow-lg shadow-purple-600/30 cursor-pointer">
              <span className="text-lg sm:text-xl">👑</span>
              Get Pro Now
            </button>
          </a>
        </footer>
      </div>
    </div>
  );
};

export default UpgradeToPro;
